import React, { useEffect, useState } from "react";
import {
  Alert, 
  AlertTitle,
  
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Card,
  CardContent,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  Grid,
  // TextareaAutosize,  
  
  Paper,
  Tab,
  Tabs,

  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  MenuItem,
  Select,
} from "@mui/material";
import { useLocalStorage } from "../../utils/LocalStorageHook";
import { Theme, useTheme } from '@mui/material/styles'
import { createStyles, makeStyles } from '@mui/styles';
import { checkForRole } from "../../utils/GetAuth";
import {
  Lock,
  ExpandMore,
  DeleteForever,
  DeleteSweep,
  Help,
} from '@mui/icons-material';
import { removeLeadingZeros } from "../../utils/Formatter"
import { CustEqupKey, iSupValidateECA, iSupDeactivateECA, iSupDeactivateAllSalesOfficeECA } from "../../utils/PostAdmin";
import { getSalesOffices, salesOffice, getDuplicateECA, DupECA, getAllECA, AllECA} from "../../utils/GetAdmin";
import TableAbstraction from "../../componentUtils/TableAbstraction";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerDiv: {
      textAlign: "center"
    },
    root: {},
    card: {},
    accordion: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    primary: {
      marginTop: theme.spacing(2),
    },
    checkbox: {
      padding: 0,
    },
    divWidth: {
      width: "100%",
    },
    gridSpaceable: {
      padding: theme.spacing(2),
    },
    gridMarginTop: {
      marginTop: theme.spacing(2),
    },
    paragraph: {
      padding: theme.spacing(1),
    },
    paragraphAct: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    paper: {
        position: 'absolute',
        backgroundColor: theme.palette.background.paper,
        border: '1px solid #000',
        boxShadow: theme.shadows[4],
        padding: theme.spacing(2, 2, 2),
    },
    modalInput: {
      width: "350px",
    },
  })
);

interface rsltKeys{
  orig:string;
  keys:string[];
  ceKey:CustEqupKey;
}
function parseStringToNumericKeys(str:string){
  const REGEX = /[^\d]+/ig;
  const SPLIT_CHAR = ';';

  let strs = str.split('\n')
  let rslt:rsltKeys[]=[]

  strs.forEach(s=>{
    let keys = s
          .replaceAll(REGEX,SPLIT_CHAR) // replace non-numeric character with ';'
          .split(SPLIT_CHAR) // split to array on ';'
          .filter(k=>k !== '') // filter out empty strings
    
    const customer_num:string =  (keys[0]||'')?lPad(keys[0], 10,'0').substr(0,10):'';
    const equipment_num:string = (keys[1]||'')?lPad(keys[1] ||'', 18,'0').substr(0,18):'';

    rslt.push({
      orig:s,
      keys,
      ceKey:{customer_num,equipment_num}
    })
  })
  return rslt;
}

interface ecaResults{
  active:boolean;
  customerNum:string;
  equipmentNum:string;
  validFromDt:string;
  validToDt:string;
} 
function lPad( theString:string, theSize:number, thePadChar:string )
{
  theSize -= theString.toString().length;
  if ( theSize > 0 )
  {
    return new Array( theSize + (/\./.test( theString ) ? 2 : 1) ).join( thePadChar ) + theString;
  }
  return theString + ""; // always return a string
}

/**
 * Builds the tab that handles the "Raw Mode" for disabling Equipment Customer Relations
 * This accepts user input, attempts to parse it, then validates against current data.
 * The user can then confirm to deactivate the ECA relationships 
 */
export function RawMode(params:{clientId:string, salesOrg:string}) {
  const classes = useStyles();
  const [doneLoading, setDoneLoading] = React.useState<boolean>();
  
  const clientId = params.clientId;
  const salesOrg = params.salesOrg;

  const [inputRaw, setInputRaw] = React.useState<string>();
  const [inputData, setInputData] = React.useState<rsltKeys[]>([]);
  const [validData, setValidData] = React.useState<ecaResults[]>([]);

  useEffect(() => {
    if (doneLoading) return;

    setDoneLoading(true);
  }, [doneLoading,salesOrg]);


  function handleInputChange(ce: React.ChangeEvent<HTMLTextAreaElement>){
    const val = ce.target.value || '';
    setInputRaw(val)
    try {
      const data = parseStringToNumericKeys(val)
      setInputData(data);
      setValidData([])
    } catch (error) {
      console.error(error)
    }
  }

  async function handleValidationClick(){
    const ce = inputData.map(data=>data.ceKey)
    const resp = await iSupValidateECA(clientId,salesOrg,ce)
    let rslt:ecaResults[] = []
    if(resp && resp.status < 400 && resp.data.result){
      rslt =  resp.data.result
    }
    setValidData(rslt)
  }

  async function handleDeactivateClick(){
    const ce = inputData.map(data=>data.ceKey)
    const resp = await iSupDeactivateECA(clientId,salesOrg,ce)
    let rslt:ecaResults[] = []
    if(resp && resp.status < 400 && resp.data.result){
      rslt =  resp.data.result
    }
    setValidData(rslt)
  }

  return (
    <Grid container spacing={2} className={classes.primary}> 
      <Grid item xs={12} xl={12}>
        <Accordion className={classes.accordion} >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="h5" className={classes.paragraph}>
              <Help /> How to Use
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Card className={classes.card}>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  className={classes.paragraphAct}
                  paragraph
                >
                  <strong>BEFORE USING</strong> make sure you have validated the correct assignments in the SAP system. 
                  This is a manual effort where you need to extract the current assignments from SAP as well as from Next Gen Vending. 
                  Once you have identified the outdated equipment customer assignments, paste the list in the below <strong>Raw Input</strong> text box. 
                  We expect numeric values only. Customer Number is first, followed by Equipment Number. 
                </Typography>
                <Typography
                  variant="subtitle1"
                  className={classes.paragraphAct}
                  paragraph
                >
                  The tool will parse the input and display what it has detected in the <strong>Parsed Input</strong> text box. 
                  It will also pad the input to the expected length, and display the CustomerNum/EquipmentNum in the table. 
                  If the displayed data looks correct, click the <strong>Validate</strong> button. 
                  The system will check for current equipment customer assignments, and will highlight data that is about to be deactivated. 
                  At this point, no data has actually been altered in any way, and it is pure informational. 
                </Typography>
                <Typography
                  variant="subtitle1"
                  className={classes.paragraphAct}
                  paragraph
                >
                  If you are happy with deactivating the assignment of the highlighted rows, click the <strong>Deactivate Selected</strong> button. 
                  At this time, the customer equipment assignment will be disabled in the system. The page will refresh, and should show no more rows selected. 
                  The only way to restore the customer equipment assignment is to maintain the data in SAP ECC. 
                </Typography>
              </CardContent>
            </Card>
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item md={12} lg={3}>
        <Typography
          variant="h6"
          className={classes.paragraphAct}
          paragraph
        > 
          Raw Input 
        </Typography>
          <TextField
                color={"primary"}
                multiline
                aria-label="empty textarea"
                minRows={5}
                fullWidth
                style={{ minWidth: "100%", maxWidth: "100%" }}
                placeholder="Empty"
                value={inputRaw}
                onChange={handleInputChange}
              />
          <Button 
            variant="contained" 
            color="primary"
            onClick={()=>handleValidationClick()}
          >
            Validate
          </Button>
      </Grid>
      <Grid item md={12} lg={3}>
        <Typography
          variant="h6"
          className={classes.paragraphAct}
          paragraph
        > 
          Parsed Input 
        </Typography>
          <TextField
                color={"primary"}
                multiline
                aria-label="empty textarea"
                minRows={5}
                fullWidth
                style={{ minWidth: "100%", maxWidth: "100%" }}
                placeholder="Empty"
                disabled={true}
                value={inputData.map(d => d.keys.join(',')).join('\n')}
              />
      </Grid>
      <Grid item md={12} lg={6}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>CustomerNum</TableCell>
              <TableCell>EquipmentNum</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inputData?inputData.map(data=>{
              const ce = data.ceKey;
              if(ce.customer_num === '0000000000'){
                return (<div/>)
              }
              return (
                <TableRow>
                  <TableCell>{ce.customer_num || ''}</TableCell>
                  <TableCell>{ce.equipment_num || ''}</TableCell>
                </TableRow>
              )
            }):<div/>}
          </TableBody>
        </Table>
      </Grid>
      {(validData && validData.length > 1)?(
      <Grid item md={12}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>CustomerNum</TableCell>
              <TableCell>EquipmentNum</TableCell>
              <TableCell>ValidFromDt</TableCell>
              <TableCell>ValidToDt</TableCell>
              <TableCell>active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validData?validData.map(data=>{
              //if(!data.active) return <div/>
              const toDel = (data.active && -1 !== inputData.findIndex(id=>id.ceKey.customer_num === data.customerNum && id.ceKey.equipment_num === data.equipmentNum))
              return (
                <TableRow selected={toDel}>
                  <TableCell>{data.customerNum || ''}</TableCell>
                  <TableCell>{data.equipmentNum || ''}</TableCell>
                  <TableCell>{data.validFromDt || ''}</TableCell>
                  <TableCell>{data.validToDt || ''}</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={data.active}
                      name="doubleColumns"
                      color="primary"
                      className={classes.checkbox}
                    />
                  </TableCell>
                </TableRow>
              )
            }):<div/>}
          </TableBody>
        </Table>
        <Button 
          variant="contained" 
          color="primary"
          onClick={()=>handleDeactivateClick()}
        >
          Deactivate Selected
        </Button>
      </Grid>
      ) : (<div/>)}
    </Grid>
    )
}

/**
 * 
 */
 export function DuplicatesMode(params:{clientId:string, salesOrg:string}) {
  const classes = useStyles();
  const [doneLoading, setDoneLoading] = React.useState<boolean>();
  
  const clientId = params.clientId;
  const salesOrg = params.salesOrg;

  const [duplicateECAs, setDuplicateECAs] = React.useState<DupECA[]>([]);
  const [ecaRPP, setEcaRPP] = React.useState<number>(25);

  useEffect(() => {
    if (doneLoading) return;
    getDuplicateECA(salesOrg).then( (dups) =>{setDuplicateECAs(dups);});
    setDoneLoading(true);
  }, [doneLoading,salesOrg]);

  function getEquipmentList(dup:DupECA){
    return dup.active_equipment_nums.map(v => {return(v.equipment_num)})
  }
  function getOldEquipmentList(dup:DupECA){
    return getEquipmentList(dup).filter(aen => aen !== dup.equipment_num)
  }
  function getOldCEKeyList(dup:DupECA){
    let ret:CustEqupKey[] = 
      getOldEquipmentList(dup).map(oe => {
        return {customer_num:dup.customer_num, equipment_num:oe}
      });
    return ret;
  }
  function getALLOldCEKeyList(dups:DupECA[]){
    let ret:CustEqupKey[] = []
    dups.forEach(dup=>{
      ret.concat(getOldCEKeyList(dup))
    })
    return ret
  }
  

  return (
    <Grid container spacing={2} className={classes.primary}> 
      <Grid item xs={12} xl={12}>
        <Accordion className={classes.accordion} >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="h5" className={classes.paragraph}>
              <Help /> How to Use
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Card className={classes.card}>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  className={classes.paragraphAct}
                  paragraph
                >
                  <strong>BEFORE USING:</strong> This tool can be used to clean up "duplicate" Equipment to Customer relationships. 
                  It is recommended to cross check ECC before deleting the relationship in this support tool.  
                </Typography>
                <Typography
                  variant="subtitle1"
                  className={classes.paragraphAct}
                  paragraph
                >
                  On page load, the tool will detect "duplicates" across all delivery Plants. If you click <DeleteForever /> 
                  under the "Deactivate Old" column, the relationship between the relevant customer and equipment will be terminated 
                  in the NextGen Vending database. 
                </Typography>
              </CardContent>
            </Card>
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item md={12} lg={12}>
        <TableAbstraction
          settingsKey="ADM_IS_ECA_DUP"
          options={{
            draggableColumns: {
              enabled: true,
            },
            rowsPerPageOptions: [25, 50, 100],
            serverSide: false,
            selectableRows: "none",
            rowsPerPage: ecaRPP,
            onChangeRowsPerPage: (numberOfRows) => {
              setEcaRPP(numberOfRows);
            },
          }}
          title={`${duplicateECAs.length} Customers with duplicate Equipment Assignments`}
          columns={[
            {
              label: "Sales Office",
              name: "sales_office",
              options: {
                filter: true,
              },
            },
            {
              label: "Delivery Plant",
              name: "delivering_plant",
              options: {
                filter: true,
              },
            },
            {
              label: "Customer",
              name: "customer_num",
              options: {
                filter: true,
              },
            },
            {
              label: "Active Count",
              name: "CNT",
              options: {
                filter: true,
              },
            },
            {
              label: "Equipment To Keep",
              name: "equipment_num",
              options: {
                filter: true,
              },
            },
            {
              label: "Active Equipments",
              name: "active_equipment_nums",
              options: {
                filter: true,
                renderCell: (params) => {
                  return getEquipmentList(params.row).map(e=>removeLeadingZeros(e)).join(', ');
                },
              },
            },
            {
              label: "Deactivate Old",
              name: "deactive_equipment_nums",
              options: {
                filter: true,
                renderCell: (params) => {
                  return (
                    <div>
                      {
                        getOldCEKeyList(params.row).map(cek => {
                        return(
                          <Button
                            color="primary"
                            variant="contained"
                            onClick={() =>{
                              iSupDeactivateECA(clientId,salesOrg,[cek])
                                .then(()=>{
                                  setDuplicateECAs([]);
                                  setDoneLoading(false)
                                })
                            }}
                          >
                            <DeleteForever /> {removeLeadingZeros(cek.equipment_num)}
                          </Button> 
                        )
                      })}
                    </div>)
                },
              },
            },
          ]}
          data={duplicateECAs}
        />
      </Grid>
    </Grid>
    )
}


/**
 * DANGEROUS! Disables all Equipment to Customer relationships by sales office.  
 * This is intended to be used only when initializing sales offices in the solution. 
 * DO NOT RUN IF YOU DO NOT FULLY UNDERSTAND THE RISK
 */
 export function SOffMode(params:{clientId:string, salesOrg:string}) {
  const classes = useStyles();
  const [doneLoading, setDoneLoading] = React.useState<boolean>();
  
  const clientId = params.clientId;
  const salesOrg = params.salesOrg;

  const [msg, setMsg] = React.useState<string>('Select Delivery Plants to preview data');
  const [sOff, setSOff] = React.useState<string>('');
  const [ecaAllRPP, setEcaAllRPP] = React.useState<number>(25);
  const [salesOffices, setSalesOffices] = React.useState<salesOffice[]>([]);

  const [ecaData, setEcaData] = React.useState<AllECA[]>([]);
console.log("sOff===>",sOff,salesOffices)
  useEffect(() => {
    if (doneLoading) return;
    getSalesOffices(salesOrg).then( (so) =>{setSalesOffices(so);});
    setDoneLoading(true);
  }, [doneLoading,salesOrg,sOff]);

  const changeSalesOffice = (so:string) => {
    setSOff(so);
    setEcaData([]);
    getAllECA(salesOrg,so).then((data)=>{
      setEcaData(data.allECA)
      setMsg(data.message || 'None found')
    })
  }


  return (
    <Grid container spacing={2} className={classes.primary}> 
      <Grid item xs={12} xl={12}>
        <Accordion className={classes.accordion} >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="h5" className={classes.paragraph}>
              <Help /> How to Use
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Card className={classes.card}>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  className={classes.paragraphAct}
                  paragraph
                >
                  <strong>BEFORE USING: do not use this tool without confirming with the functional lead / application owner.</strong> 
                </Typography>
                <Typography
                  variant="subtitle1"
                  className={classes.paragraphAct}
                  paragraph
                >
                  There is a hard coded list of users that are able to click the button. 
                  If you are not on the list, the API SHOULD reject your attempt. 
                  This is by design. This tool should not be used regularly.
                  It is all but guarenteed to break operational locations. 
                </Typography>
                <Typography
                  variant="subtitle1"
                  className={classes.paragraphAct}
                  paragraph
                >
                  If you are sure this tool should be used, first select a sales office from the dropdown. 
                  Once the data is loaded, you will be able to click the delete button. 
                  Once you click the delete button, the data is gone, and must be resent from ECC.
                </Typography>
              </CardContent>
            </Card>
          </AccordionDetails>
        </Accordion>
      </Grid>
      <Grid item xs={12} xl={12}>
        <Alert severity="error">
          <AlertTitle>BEFORE USING</AlertTitle>   
          This tool should not be run on live locations. This is for data corrections only. Please read "How to Use" if you have not used this yet.
        </Alert>
      </Grid>
      <Grid item md={6} lg={6}>
        <div>Delivery Plant</div>
        <Select
          label="Delivery Plant"
          required
          placeholder="Delivery Plant"
          value={sOff}
          inputProps={{ name: "Delivery Plant" }}
          className={classes.modalInput}
          onChange={(e) => {
            changeSalesOffice(
              e.target.value &&
                typeof e.target.value == "string"
                ? e.target.value
                : ""
            );
          }}
        >
          {salesOffices ? (
            Object.entries(salesOffices).map(
              (so: any, i: number) => (
                <MenuItem
                  key={so[1].code}
                  value={so[1].code}
                >
                  {so[1].code + " - " + so[1].text_long}
                </MenuItem>
              )
            )
          ) : (
            <div></div>
          )}
        </Select>
      </Grid>
      <Grid item md={6} lg={6}>
        <Button
          color="primary"
          variant="contained"
          disabled={ecaData.length === 0}
          onClick={() =>{
            iSupDeactivateAllSalesOfficeECA(clientId,salesOrg,sOff).then(()=>changeSalesOffice(sOff))
          }}
        >
          <DeleteSweep /> Delete {ecaData.length} ECA Relations
        </Button>
      </Grid>
      <Grid item md={12} lg={12}>
        <TableAbstraction
        settingsKey="ADM_IS_ECA_ALL"
          options={{
            draggableColumns: {
              enabled: true,
            },
            rowsPerPageOptions: [25, 50, 100],
            serverSide: false,
            selectableRows: "none",
            rowsPerPage: ecaAllRPP,
            onChangeRowsPerPage: (numberOfRows) => {
              setEcaAllRPP(numberOfRows);
            },
          }}
          title={msg}
          columns={[
            {
              label: "Route",
              name: "master_route",
              options: {
                filter: true,
              },
            },
            {
              label: "Customer #",
              name: "customer_num",
              options: {
                filter: true,
              },
            },
            {
              label: "Customer Name",
              name: "name1",
              options: {
                filter: true,
              },
            },
            {
              label: "Valid From",
              name: "valid_from_dt",
              options: {
                filter: true,
              },
            },
            {
              label: "Valid To",
              name: "valid_to_dt",
              options: {
                filter: true,
              },
            },
            {
              label: "Equipment #",
              name: "equipment_num",
              options: {
                filter: true,
              },
            },
            {
              label: "Asset #",
              name: "asset_num",
              options: {
                filter: true,
              },
            },
            {
              label: "Manufacturer",
              name: "manufacturer",
              options: {
                filter: true,
              },
            },
            {
              label: "Model",
              name: "model",
              options: {
                filter: true,
              },
            },
            {
              label: "Status",
              name: "status",
              options: {
                filter: true,
              },
            },
          ]}
          data={ecaData}
        />
      </Grid>
    </Grid>
    )
}


export default function ISupECA() {
  const classes = useStyles();
  const [clientId,] = useLocalStorage("SETTINGS.clientId", "100");
  const [salesOrg,] = useLocalStorage("SETTINGS.salesOrg", "");
  const enabled = checkForRole(clientId,salesOrg,'cona_internal_dev');
  const [doneLoading, setDoneLoading] = React.useState<boolean>();

  const [curTab, setCurTab] = React.useState<number>(0);
  const subtabs = [
    {
      title: "Duplicate ECA",
      content: <DuplicatesMode clientId={clientId} salesOrg={salesOrg} />,
    },
    {
      title: "Raw User Input",
      content: <RawMode clientId={clientId} salesOrg={salesOrg} />,
    },
    {
      title: "Entire Delivery Plant",
      content: <SOffMode clientId={clientId} salesOrg={salesOrg} />,
    },
  ];

  const handleTabsChange = (_: React.ChangeEvent<{}>, value: any) => {
    let idx = subtabs?.findIndex((v: any) => v.title === value) || 0;
    setCurTab(idx);
  };

  useEffect(() => {
    if (doneLoading) return;

    setDoneLoading(true);
  }, [doneLoading,salesOrg]);

  return (
     <div>
       {enabled ? (
        <div className={classes.root}>
          <Typography variant="h4" className={classes.paragraph}>
            CONA Support Tools: Deactivate Equipment Customer Assignment 
          </Typography>
          <Typography
            variant="subtitle1"
            className={classes.paragraphAct}
            paragraph
          >
            This page allows you to bulk disable the relationship between customer and equipment. 
            <strong>Use this tool with caution as you ARE modifying master data.</strong> 
          </Typography>
          <Paper className={classes.root}>
            <Tabs
                onChange={handleTabsChange}
                value={subtabs[curTab].title}
                variant="fullWidth"
              >
                {subtabs.map(
                  (tab) =>
                      <Tab
                        key={tab.title}
                        label={tab.title}
                        value={tab.title}
                      />
                )}
              </Tabs>
              <Alert severity="warning">
                <AlertTitle>Use this tool with caution: You are modifying master data.</AlertTitle>   
                There is no undo button. The only way to recover data is to send it again from SAP ECC.
              </Alert>
              {subtabs[curTab].content}
            </Paper>
        </div>
        ) : (
        <Alert severity="error">
          <AlertTitle><Lock/> Access Error</AlertTitle>   
          You do not currently have access to <strong>CONA Internal Support</strong> activities for the <strong>{salesOrg}</strong> bottler. 
        </Alert>
      )}
     </div>
    )
}